const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

let _accessToken = null;
const _tokenSubscribers = new Set(); // callbacks to notify on token changes

export function setAccessToken(token) {
  _accessToken = token;
  _tokenSubscribers.forEach((onTokenChange) => {
    try {
      onTokenChange(token);
    } catch (e) {
      console.error("Error in access token subscriber:", e);
    }
  });
}

export function getAccessToken() {
  // Used by Auth Context for global auth state
  return _accessToken;
}

export function subscribeAccessToken(onTokenChange) {
  // Auth Context uses this to update state on token changes
  _tokenSubscribers.add(onTokenChange);
  return () => _tokenSubscribers.delete(onTokenChange);
}

export function clearTokens() {
  _accessToken = null;
  setAccessToken(null); // notify subscribers
}

function buildLoginRedirectUrl() {
  const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return `/login?next=${encodeURIComponent(nextPath)}`;
}

export async function refreshAccessToken() {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: "POST",
      credentials: "include", // send HttpOnly refresh cookie
      headers: { "Content-Type": "application/json" },
    });

    if (!res.ok) {
      clearTokens();
      throw new Error("Refresh token invalid or expired");
    }

    const data = await res.json();
    setAccessToken(data.accessToken);

    return {
      accessToken: data.accessToken,
      user: {
        userId: data.userId,
        username: data.username,
        email: data.email,
        role: data.role,
        customerPkId: data.customerPkId,
        customerName: data.customerName,
        baseLocationId: data.baseLocationId,
      },
    };
  } catch (err) {
    clearTokens();
    throw err;
  }
}

export async function apiFetch(endpoint, options = {}) {
  const method = options.method || "GET";

  let fetchOptions = {
    method,
    credentials: "include", // ensure cookies are sent to backend if needed
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...(method !== "GET" && options.body ? { body: options.body } : {}),
  };

  // attach access token from memory if available
  if (_accessToken) {
    fetchOptions.headers.Authorization = `Bearer ${_accessToken}`;
  }

  let res = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);

  if (res.status === 401) {
    try {
      console.log("refreshing access token...");
      const newToken = await refreshAccessToken();
      fetchOptions.headers.Authorization = `Bearer ${newToken.accessToken}`;
      res = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
    } catch (err) {
      // refresh failed: force logout/redirect
      window.location.href = buildLoginRedirectUrl();
      return;
    }
  }

  return res;
}
